import * as React from 'react';
import { useState, useCallback, useMemo } from 'react';
import * as R from 'ramda';
import { DevItemComment } from './models';
import LocalDiv from '../../../localization/LocalDiv';
import DevItemCommentView from './DevItemCommentView';
import Msg from '../../../localization/Msg';
import AddDevItemCommentForm from './AddDevItemCommentForm';
import { css } from '@emotion/core';
import Spacer from '../../../layout/Spacer';
import { COLORS } from '../../../globalStyle/colors';
import { useCurrentCollaborator } from './hooks';
import { useCurrentUser } from '../../../auth/hooks';
import { isCurrentUserComment, isParticipantComment } from './module';
import { Str } from '../../../localization/translationKeys';


const styles = {
  header: [
    css`font-weight: bold;`,
  ],
  form: [
    css`
      padding: 12px;
      justify-content: stretch;
    `,
  ],
  comment: [
    css`
      border-top: 1px solid;
      border-image-slice: 1;
      border-image-source: linear-gradient(to right, transparent, ${COLORS.PROJECTBAR_GREY} 3% 97%, transparent);
    `,
  ],
};


type Props = {
  projectId: number;
  devItemId: number;
  devActionId: number;
  comments: DevItemComment[];
  addComment: (comment: string) => void;
  updateComment: (commentId: number, comment: string) => void;
  onCommentAdded: (comment: string) => void;
  className?: string;
};


const renderComments = (
  comments: DevItemComment[],
  inEditMode: (comment: DevItemComment) => boolean,
  handleClick: (comment: DevItemComment) => void,
  handleCancel: () => void,
  updateComment: (commentId: number, comment: string) => void,
) => comments.map(
  comment => (
    <DevItemCommentView
      key={comment.commentId}
      comment={comment}
      inEditMode={inEditMode(comment)}
      handleClick={() => handleClick(comment)}
      handleCancel={handleCancel}
      updateComment={(editedComment: string) => updateComment(comment.commentId, editedComment)}
      css={[styles.form, styles.comment]}
    />
  ),
);


/**
 * DevItemComments: a component that renders comments added by collaborators and the participant, to the current dev
 * item action
 */
const DevItemComments: React.FC<Props> = (
  {
    projectId,
    devItemId,
    devActionId,
    comments,
    addComment,
    updateComment,
    onCommentAdded,
    className,
  },
) => {
  const [underEdit, setUnderEdit] = useState<DevItemComment | undefined>();

  const currentUser = useCurrentUser();

  const currentCollaborator = useCurrentCollaborator();

  const currentEmail = useMemo(
    () => currentUser?.userName || currentCollaborator?.email || '',
    [currentCollaborator, currentUser],
  );

  const isUnderEdit = useCallback(
    (comment: DevItemComment) => comment === underEdit,
    [underEdit],
  );

  const canEditComment = useCallback(
    (comment: DevItemComment) => isCurrentUserComment(comment, currentEmail)
                                 || (isParticipantComment(comment) && !!currentUser),
    [currentEmail, currentUser],
  );

  const handleClick = useCallback(
    (comment: DevItemComment) => {
      if (comment !== underEdit && canEditComment(comment)) {
        setUnderEdit(comment);
      }
    },
    [canEditComment, underEdit],
  );

  const handleCancel = () => setUnderEdit(undefined);

  const sortedComments = useMemo(() => R.sort(R.descend(R.prop('commentId')), comments), [comments]);

  return (
    <LocalDiv className={className}>
      <Msg str={Str.Comments} css={styles.header} />
      <Spacer sizePx={16} />
      <AddDevItemCommentForm
        projectId={projectId}
        devItemId={devItemId}
        devActionId={devActionId}
        addComment={addComment}
        onCommentAdded={onCommentAdded}
        css={styles.form}
      />
      <Spacer sizePx={16} />
      {renderComments(sortedComments, isUnderEdit, handleClick, handleCancel, updateComment)}
    </LocalDiv>
  );
};

export default DevItemComments;
